/**
 * The run-scope library's pure half (bata-reqs ADR 1003; bata-platform ADR 9033–9036; this
 * repository's ADR 6028): what the editor holds while a Scope or a named selection is written, how
 * it becomes the platform's one definition spelling, what the editor can check alone before
 * anything is sent, and the words for each attribute, clause and refusal.
 *
 * A **Scope** is a set of include and exclude rules; each rule pairs a location side with a product
 * side, each side a condition (a conjunction of clauses) or a named selection of the same axis. A
 * **store selection** or **product selection** is one named condition. Every market has the system
 * selections «Intero paese» and «Tutti i prodotti» and the default Scope pairing them: written by
 * the platform, read here, never edited.
 *
 * Nothing here reads the network or the clock.
 */

import type {
  ClauseBody, CountsView, EntryChangeRequest, EntryView, PreviewRequest, RuleBody, SideBody,
} from '@/api/generated';

import { articleKeys } from '../campaigns/compose';

// ── kinds and axes ───────────────────────────────────────────────────────────

export const ENTRY_KINDS = ['scope', 'store_selection', 'product_selection'] as const;
export type EntryKind = (typeof ENTRY_KINDS)[number];

export function isEntryKind(value: string | null | undefined): value is EntryKind {
  return value !== null && value !== undefined && (ENTRY_KINDS as readonly string[]).includes(value);
}

export const AXES = ['location', 'product'] as const;
export type Axis = (typeof AXES)[number];

/** The axis a selection kind names. */
export const SELECTION_KIND_OF: Readonly<Record<Axis, EntryKind>> = {
  location: 'store_selection',
  product: 'product_selection',
};

export function axisOfKind(kind: EntryKind): Axis | null {
  if (kind === 'store_selection') return 'location';
  if (kind === 'product_selection') return 'product';
  return null;
}

/** The list cap (ADR 1003 ruling 16); the vocabularies read states it, this is the fallback. */
export const LIST_CAP = 2000;
/** The platform's text limits (bata-platform ADR 9035). */
export const NAME_MAX = 120;
export const DESCRIPTION_MAX = 1000;
/** An entry id: lower-case letters, digits and hyphens, at most 64 (ADR 9033). */
export const ENTRY_ID = /^[a-z0-9-]{1,64}$/;
/** The system ids, the same in every market, never reused (ADR 9033). */
export const RESERVED_IDS: readonly string[] = ['whole-country', 'all-products', 'default'];
/** A code value the master marks "sought and not found"; a clause matches it only when named. */
export const UNKNOWN = 'UNKNOWN';
/** The notice the platform puts on the category attributes (ADR 1003, Consequences; bata-platform#434). */
export const CLEARANCE_NOTICE = 'clearance_subcategory_overwrite';

// ── the draft ────────────────────────────────────────────────────────────────

export type ClauseDraft =
  | { readonly kind: 'in'; readonly attribute: string; readonly values: readonly string[] }
  | { readonly kind: 'date_range'; readonly attribute: string; readonly from: string; readonly to: string }
  | { readonly kind: 'number_range'; readonly attribute: string; readonly min: string; readonly max: string }
  | { readonly kind: 'list'; readonly text: string }
  /** `schemeVersion` is `scheme@version`; '' until one is chosen. */
  | { readonly kind: 'cluster'; readonly schemeVersion: string; readonly clusters: readonly string[] }
  /** The system selections' "everything" — read, never authored (ADR 9033). */
  | { readonly kind: 'every' };

export type SideDraft =
  | { readonly mode: 'selection'; readonly selection: string }
  | { readonly mode: 'condition'; readonly clauses: readonly ClauseDraft[] };

export interface RuleDraft {
  readonly effect: 'include' | 'exclude';
  readonly location: SideDraft;
  readonly product: SideDraft;
}

export interface EntryDraft {
  readonly kind: EntryKind;
  readonly entryId: string;
  readonly name: string;
  readonly description: string;
  /** A Scope's rules; empty for a selection. */
  readonly rules: readonly RuleDraft[];
  /** A selection's condition; empty for a Scope. */
  readonly clauses: readonly ClauseDraft[];
}

/** The system selection each axis starts on, so a new rule is whole from its first render. */
export const SYSTEM_SELECTION: Readonly<Record<Axis, string>> = { location: 'whole-country', product: 'all-products' };

export function newRule(effect: RuleDraft['effect']): RuleDraft {
  return {
    effect,
    location: { mode: 'selection', selection: SYSTEM_SELECTION.location },
    product: { mode: 'selection', selection: SYSTEM_SELECTION.product },
  };
}

export function emptyDraft(kind: EntryKind): EntryDraft {
  return { kind, entryId: '', name: '', description: '', rules: kind === 'scope' ? [newRule('include')] : [], clauses: [] };
}

/** A clause of an attribute, empty, of the clause kind the attribute takes. */
export function newClause(attribute: string, attributeKind: string): ClauseDraft {
  if (attribute === LIST_ATTRIBUTE) return { kind: 'list', text: '' };
  if (attribute === CLUSTER_ATTRIBUTE) return { kind: 'cluster', schemeVersion: '', clusters: [] };
  if (attributeKind === 'date') return { kind: 'date_range', attribute, from: '', to: '' };
  if (attributeKind === 'number') return { kind: 'number_range', attribute, min: '', max: '' };
  return { kind: 'in', attribute, values: [] };
}

/** The two clauses that are not an attribute of the master, offered beside the attributes. */
export const LIST_ATTRIBUTE = '__list';
export const CLUSTER_ATTRIBUTE = '__cluster';

/** The attribute a clause draft is chosen under in the editor's first select. */
export function clauseAttribute(clause: ClauseDraft): string {
  if (clause.kind === 'list') return LIST_ATTRIBUTE;
  if (clause.kind === 'cluster') return CLUSTER_ATTRIBUTE;
  if (clause.kind === 'every') return '';
  return clause.attribute;
}

// ── from the platform's spelling ─────────────────────────────────────────────

export function clauseFromBody(body: ClauseBody): ClauseDraft {
  switch (body.kind) {
    case 'every': return { kind: 'every' };
    case 'list': return { kind: 'list', text: (body.keys ?? []).join('\n') };
    case 'cluster': return { kind: 'cluster', schemeVersion: `${body.scheme ?? ''}@${body.version ?? ''}`, clusters: body.clusters ?? [] };
    case 'date_range': return { kind: 'date_range', attribute: body.attribute ?? '', from: body.from ?? '', to: body.to ?? '' };
    case 'number_range': return { kind: 'number_range', attribute: body.attribute ?? '', min: body.min ?? '', max: body.max ?? '' };
    default: return { kind: 'in', attribute: body.attribute ?? '', values: body.values ?? [] };
  }
}

function sideFromBody(body: SideBody): SideDraft {
  return body.selection !== null && body.selection !== undefined && body.selection !== ''
    ? { mode: 'selection', selection: body.selection }
    : { mode: 'condition', clauses: (body.condition ?? []).map(clauseFromBody) };
}

export function ruleFromBody(body: RuleBody): RuleDraft {
  return { effect: body.effect, location: sideFromBody(body.location), product: sideFromBody(body.product) };
}

/** An entry as the editor holds it, to edit it. */
export function draftFromEntry(entry: EntryView): EntryDraft | null {
  if (!isEntryKind(entry.kind)) return null;
  return {
    kind: entry.kind,
    entryId: entry.entryId,
    name: entry.name ?? '',
    description: entry.description ?? '',
    rules: (entry.rules ?? []).map(ruleFromBody),
    clauses: (entry.condition ?? []).map(clauseFromBody),
  };
}

// ── to the platform's spelling ───────────────────────────────────────────────

/** A list clause's keys as typed: one per line or separated by commas or spaces, repeats once. */
export function listKeys(text: string): readonly string[] {
  return [...new Set(articleKeys(text))];
}

function splitSchemeVersion(value: string): { readonly scheme: string; readonly version: string } {
  const at = value.lastIndexOf('@');
  return at < 0 ? { scheme: value, version: '' } : { scheme: value.slice(0, at), version: value.slice(at + 1) };
}

export function clauseToBody(clause: ClauseDraft): ClauseBody {
  switch (clause.kind) {
    case 'every': return { kind: 'every' };
    case 'list': return { kind: 'list', keys: listKeys(clause.text) };
    case 'cluster': {
      const { scheme, version } = splitSchemeVersion(clause.schemeVersion);
      return { kind: 'cluster', scheme, version, clusters: [...clause.clusters] };
    }
    case 'date_range': return {
      kind: 'date_range', attribute: clause.attribute, from: clause.from === '' ? null : clause.from, to: clause.to === '' ? null : clause.to,
    };
    case 'number_range': return {
      kind: 'number_range', attribute: clause.attribute, min: clause.min.trim() === '' ? null : clause.min.trim(), max: clause.max.trim() === '' ? null : clause.max.trim(),
    };
    default: return { kind: 'in', attribute: clause.attribute, values: [...clause.values] };
  }
}

function sideToBody(side: SideDraft): SideBody {
  return side.mode === 'selection' ? { selection: side.selection } : { condition: side.clauses.map(clauseToBody) };
}

export function ruleToBody(rule: RuleDraft): RuleBody {
  return { effect: rule.effect, location: sideToBody(rule.location), product: sideToBody(rule.product) };
}

/** The definition the draft states, in the spelling preview and proposal both take. */
export function definitionOf(draft: Pick<EntryDraft, 'kind' | 'rules' | 'clauses'>): Pick<PreviewRequest, 'rules' | 'condition'> {
  return draft.kind === 'scope'
    ? { rules: draft.rules.map(ruleToBody) }
    : { condition: draft.clauses.map(clauseToBody) };
}

export function previewRequestOf(draft: Pick<EntryDraft, 'kind' | 'rules' | 'clauses'>, countryKey: string, asOf: string): PreviewRequest {
  return { countryKey, asOf, kind: draft.kind, ...definitionOf(draft) };
}

// ── what the editor checks alone ─────────────────────────────────────────────

/** A problem the editor finds before sending, by the platform's own reason code where it has one. */
export type DraftProblem =
  | 'name_required' | 'text_too_long' | 'id_invalid' | 'id_reserved' | 'id_taken'
  | 'no_rules' | 'empty_condition' | 'empty_values' | 'range_unbounded' | 'range_inverted'
  | 'list_empty' | 'list_too_long' | 'cluster_version' | 'selection_unknown';

export interface DraftCheck {
  /** Problems of the entry itself — its name, id, texts, rule count. */
  readonly entry: readonly DraftProblem[];
  /** Each clause's problem, by its path: `r{rule}.{axis}.{clause}` or `c{clause}` for a selection. */
  readonly clauses: ReadonlyMap<string, DraftProblem>;
  /** Each rule side naming no selection. */
  readonly sides: ReadonlyMap<string, DraftProblem>;
  readonly ok: boolean;
}

export function clausePath(rule: number | null, axis: Axis | null, clause: number): string {
  return rule === null ? `c${clause}` : `r${rule}.${axis ?? ''}.${clause}`;
}

export function sidePath(rule: number, axis: Axis): string {
  return `r${rule}.${axis}`;
}

const DECIMAL = /^-?\d+(\.\d+)?$/;

export function clauseProblem(clause: ClauseDraft, cap: number = LIST_CAP): DraftProblem | null {
  switch (clause.kind) {
    case 'every': return null;
    case 'in': return clause.values.length === 0 ? 'empty_values' : null;
    case 'list': {
      const keys = listKeys(clause.text);
      if (keys.length === 0) return 'list_empty';
      return keys.length > cap ? 'list_too_long' : null;
    }
    case 'cluster': {
      const { scheme, version } = splitSchemeVersion(clause.schemeVersion);
      if (scheme === '' || version === '') return 'cluster_version';
      return clause.clusters.length === 0 ? 'empty_values' : null;
    }
    case 'date_range':
      if (clause.from === '' && clause.to === '') return 'range_unbounded';
      return clause.from !== '' && clause.to !== '' && clause.from > clause.to ? 'range_inverted' : null;
    case 'number_range': {
      const min = clause.min.trim();
      const max = clause.max.trim();
      if (min === '' && max === '') return 'range_unbounded';
      if ((min !== '' && !DECIMAL.test(min)) || (max !== '' && !DECIMAL.test(max))) return 'range_unbounded';
      return min !== '' && max !== '' && Number(min) > Number(max) ? 'range_inverted' : null;
    }
    default: return null;
  }
}

export interface CheckContext {
  /** Creating: the id must be new, well formed and not reserved. */
  readonly creating: boolean;
  /** Every id the market's library already holds for this kind, retired included. */
  readonly takenIds: ReadonlySet<string>;
  /** The selections a side may name, by axis (system ones included). */
  readonly selections: Readonly<Record<Axis, ReadonlySet<string>>>;
  readonly cap?: number;
}

export function checkDraft(draft: EntryDraft, context: CheckContext): DraftCheck {
  const entry: DraftProblem[] = [];
  const clauses = new Map<string, DraftProblem>();
  const sides = new Map<string, DraftProblem>();
  const cap = context.cap ?? LIST_CAP;
  if (draft.name.trim() === '') entry.push('name_required');
  if (draft.name.length > NAME_MAX || draft.description.length > DESCRIPTION_MAX) entry.push('text_too_long');
  if (context.creating) {
    if (!ENTRY_ID.test(draft.entryId)) entry.push('id_invalid');
    else if (RESERVED_IDS.includes(draft.entryId)) entry.push('id_reserved');
    else if (context.takenIds.has(draft.entryId)) entry.push('id_taken');
  }
  const conditionProblems = (list: readonly ClauseDraft[], rule: number | null, axis: Axis | null): void => {
    list.forEach((clause, index) => {
      const problem = clauseProblem(clause, cap);
      if (problem !== null) clauses.set(clausePath(rule, axis, index), problem);
    });
  };
  if (draft.kind === 'scope') {
    if (draft.rules.length === 0) entry.push('no_rules');
    draft.rules.forEach((rule, index) => {
      for (const axis of AXES) {
        const side = rule[axis];
        if (side.mode === 'selection') {
          if (!context.selections[axis].has(side.selection)) sides.set(sidePath(index, axis), 'selection_unknown');
        } else if (side.clauses.length === 0) {
          sides.set(sidePath(index, axis), 'empty_condition');
        } else {
          conditionProblems(side.clauses, index, axis);
        }
      }
    });
  } else if (draft.clauses.length === 0) {
    entry.push('empty_condition');
  } else {
    conditionProblems(draft.clauses, null, null);
  }
  return { entry, clauses, sides, ok: entry.length === 0 && clauses.size === 0 && sides.size === 0 };
}

/** Whether the draft can be counted: every clause whole, every side stated. Names do not matter. */
export function countableDraft(draft: EntryDraft, context: Omit<CheckContext, 'creating' | 'takenIds'>): boolean {
  const check = checkDraft({ ...draft, name: draft.name === '' ? '-' : draft.name }, { ...context, creating: false, takenIds: new Set() });
  return check.clauses.size === 0 && check.sides.size === 0 && !check.entry.includes('no_rules') && !check.entry.includes('empty_condition');
}

/** An id from a name: lower case, no accents, words joined by hyphens, at most 64. */
export function idFromName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/-+$/g, '');
}

// ── the proposal ─────────────────────────────────────────────────────────────

export type ProposalAction = 'create' | 'edit' | 'retire';

export function changeOf(draft: EntryDraft, action: ProposalAction): EntryChangeRequest {
  if (action === 'retire') return { action, kind: draft.kind, entryId: draft.entryId };
  return {
    action,
    kind: draft.kind,
    entryId: draft.entryId,
    name: draft.name.trim(),
    description: draft.description.trim() === '' ? null : draft.description.trim(),
    ...definitionOf(draft),
  };
}

// ── the change set's items ───────────────────────────────────────────────────

export const TARGET_STEM = 'run_scope';

export interface RunScopeTarget {
  readonly countryKey: string;
  readonly kind: EntryKind;
  readonly entryId: string;
}

/** `run_scope.<country>.<kind>.<id>` (bata-platform ADR 9035), or `null`. */
export function parseRunScopeTarget(targetKey: string): RunScopeTarget | null {
  const parts = targetKey.split('.');
  if (parts.length !== 4 || parts[0] !== TARGET_STEM) return null;
  const [, countryKey, kind, entryId] = parts as [string, string, string, string];
  if (countryKey === '' || !isEntryKind(kind) || !ENTRY_ID.test(entryId)) return null;
  return { countryKey, kind, entryId };
}

/** A cells count's change, `null` where either side is not stated. */
export function countDelta(before: CountsView | null | undefined, after: CountsView | null | undefined, measure: keyof CountsView): number | null {
  const was = before?.[measure];
  const now = after?.[measure];
  if (was === null || was === undefined || now === null || now === undefined) return null;
  return now - was;
}

/** How an entry is named: the author's name, or the system entry's label in the reader's language. */
export function entryName(entry: Pick<EntryView, 'name' | 'label' | 'entryId'>): string {
  if (entry.label !== '') return entry.label;
  return entry.name ?? entry.entryId;
}

// ── words ────────────────────────────────────────────────────────────────────

export const KIND_KEY: Readonly<Record<EntryKind, string>> = {
  scope: 'inseason.runScopes.kind.scope',
  store_selection: 'inseason.runScopes.kind.store_selection',
  product_selection: 'inseason.runScopes.kind.product_selection',
};

export const KIND_PLURAL_KEY: Readonly<Record<EntryKind, string>> = {
  scope: 'inseason.runScopes.kindPlural.scope',
  store_selection: 'inseason.runScopes.kindPlural.store_selection',
  product_selection: 'inseason.runScopes.kindPlural.product_selection',
};

export const AXIS_KEY: Readonly<Record<Axis, string>> = {
  location: 'inseason.runScopes.axis.location',
  product: 'inseason.runScopes.axis.product',
};

export const EFFECT_KEY: Readonly<Record<RuleDraft['effect'], string>> = {
  include: 'inseason.runScopes.effect.include',
  exclude: 'inseason.runScopes.effect.exclude',
};

export const ACTION_KEY: Readonly<Record<string, string>> = {
  create: 'inseason.runScopes.action.create',
  edit: 'inseason.runScopes.action.edit',
  retire: 'inseason.runScopes.action.retire',
};

/** Every attribute ADR 1003 §2 offers, by master column; one not listed is said by its column. */
export const ATTRIBUTE_KEY: Readonly<Record<string, string>> = {
  [LIST_ATTRIBUTE]: 'inseason.runScopes.attribute.list',
  [CLUSTER_ATTRIBUTE]: 'inseason.runScopes.attribute.cluster',
  country_key: 'inseason.runScopes.attribute.country_key',
  location_type: 'inseason.runScopes.attribute.location_type',
  channel_key: 'inseason.runScopes.attribute.channel_key',
  format_code: 'inseason.runScopes.attribute.format_code',
  climate_zone_code: 'inseason.runScopes.attribute.climate_zone_code',
  dc_assignment_key: 'inseason.runScopes.attribute.dc_assignment_key',
  district_code: 'inseason.runScopes.attribute.district_code',
  has_sale_zone: 'inseason.runScopes.attribute.has_sale_zone',
  opening_date: 'inseason.runScopes.attribute.opening_date',
  closing_date: 'inseason.runScopes.attribute.closing_date',
  capacity_units: 'inseason.runScopes.attribute.capacity_units',
  category_code: 'inseason.runScopes.attribute.category_code',
  sub_category_code: 'inseason.runScopes.attribute.sub_category_code',
  department_family: 'inseason.runScopes.attribute.department_family',
  brand_tier: 'inseason.runScopes.attribute.brand_tier',
  gender: 'inseason.runScopes.attribute.gender',
  age_group: 'inseason.runScopes.attribute.age_group',
  colour_family: 'inseason.runScopes.attribute.colour_family',
  material_upper: 'inseason.runScopes.attribute.material_upper',
  material_sole: 'inseason.runScopes.attribute.material_sole',
  construction: 'inseason.runScopes.attribute.construction',
  silhouette: 'inseason.runScopes.attribute.silhouette',
  last_fit_family: 'inseason.runScopes.attribute.last_fit_family',
  heel_height_band: 'inseason.runScopes.attribute.heel_height_band',
  occasion: 'inseason.runScopes.attribute.occasion',
  price_band: 'inseason.runScopes.attribute.price_band',
  season_code: 'inseason.runScopes.attribute.season_code',
  launch_date: 'inseason.runScopes.attribute.launch_date',
  lifecycle_class: 'inseason.runScopes.attribute.lifecycle_class',
  risk_desc: 'inseason.runScopes.attribute.risk_desc',
  carry_over_flag: 'inseason.runScopes.attribute.carry_over_flag',
  is_footwear: 'inseason.runScopes.attribute.is_footwear',
};

/** The words for a problem the editor finds, and for a refusal reason the platform names. */
export const REASON_KEY: Readonly<Record<string, string>> = {
  name_required: 'inseason.runScopes.reason.name_required',
  text_too_long: 'inseason.runScopes.reason.text_too_long',
  id_invalid: 'inseason.runScopes.reason.id_invalid',
  id_reserved: 'inseason.runScopes.reason.id_reserved',
  id_taken: 'inseason.runScopes.reason.id_taken',
  no_rules: 'inseason.runScopes.reason.no_rules',
  empty_condition: 'inseason.runScopes.reason.empty_condition',
  empty_values: 'inseason.runScopes.reason.empty_values',
  range_unbounded: 'inseason.runScopes.reason.range_unbounded',
  range_inverted: 'inseason.runScopes.reason.range_inverted',
  list_empty: 'inseason.runScopes.reason.list_empty',
  list_too_long: 'inseason.runScopes.reason.list_too_long',
  cluster_version: 'inseason.runScopes.reason.cluster_version',
  selection_unknown: 'inseason.runScopes.reason.selection_unknown',
  selection_retired: 'inseason.runScopes.reason.selection_retired',
  selection_axis_mismatch: 'inseason.runScopes.reason.selection_axis_mismatch',
  attribute_unknown: 'inseason.runScopes.reason.attribute_unknown',
  clause_not_for_attribute: 'inseason.runScopes.reason.clause_not_for_attribute',
  clause_not_for_axis: 'inseason.runScopes.reason.clause_not_for_axis',
  value_blank: 'inseason.runScopes.reason.value_blank',
  flag_value_unknown: 'inseason.runScopes.reason.flag_value_unknown',
  every_reserved: 'inseason.runScopes.reason.every_reserved',
  effect_unknown: 'inseason.runScopes.reason.effect_unknown',
  axis_unknown: 'inseason.runScopes.reason.axis_unknown',
  definition_unreadable: 'inseason.runScopes.reason.definition_unreadable',
  kind_unknown: 'inseason.runScopes.reason.kind_unknown',
  action_unknown: 'inseason.runScopes.reason.action_unknown',
  entry_changed_twice: 'inseason.runScopes.reason.entry_changed_twice',
  entry_unknown: 'inseason.runScopes.reason.entry_unknown',
  entry_retired: 'inseason.runScopes.reason.entry_retired',
  system_entry: 'inseason.runScopes.reason.system_entry',
  definition_required: 'inseason.runScopes.reason.definition_required',
  definition_not_of_kind: 'inseason.runScopes.reason.definition_not_of_kind',
  entry_unchanged: 'inseason.runScopes.reason.entry_unchanged',
  entry_in_use: 'inseason.runScopes.reason.entry_in_use',
  entry_changed_since_proposal: 'inseason.runScopes.reason.entry_changed_since_proposal',
  market_unknown: 'inseason.runScopes.reason.market_unknown',
  item_unreadable: 'inseason.runScopes.reason.item_unreadable',
};

export function reasonKey(reason: string): string {
  return REASON_KEY[reason] ?? 'inseason.runScopes.reason.unknown';
}
